<script setup lang="ts">
import UICheckbox from '@/shared/ui/Checkbox/Checkbox.vue';
import UIToggler from '@/shared/ui/Toggler/Toggler.vue';
import UIButton from '@/shared/ui/Button/Button.vue';
import PriceFilter from '@/pages/catalog-detail/ui/Filters/Price.vue';
import CrossIcon from '@/shared/ui/Icons/CrossRounded.vue';
import type { Props } from './model/types/filters';
import { useFilters } from './composables/useFilters';

withDefaults(defineProps<Props>(), {
  modelValue: false,
});

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const {
  minPrice,
  maxPrice,
  defaultMinPrice,
  defaultMaxPrice,
  brands,
  toggles,
  applyFilters,
  clearFilters,
} = await useFilters();

const { t } = useI18n();
</script>

<template>
  <div class="common">
    <button
      type="button"
      class="common__button _close"
      :aria-label="t('closeFilters')"
      @click.prevent="emit('close')"
    >
      <CrossIcon class="common__icon _close" />
    </button>
    <div class="common__title _main">{{ t('filters') }}</div>
    <div class="common__filters">
      <div class="common__block">
        <div class="common__label">{{ t('price') }}</div>
        <PriceFilter
          v-model:minPrice="minPrice"
          v-model:maxPrice="maxPrice"
          :defaultMinPrice="defaultMinPrice"
          :defaultMaxPrice="defaultMaxPrice"
        />
      </div>
      <div class="common__block">
        <div class="common__label">{{ t('brands') }}</div>
        <ul class="common__brands">
          <li v-for="brand in brands" :key="brand.id" class="common__brand">
            <UICheckbox v-model="brand.checked" :text="brand.name" />
          </li>
        </ul>
      </div>
      <div class="common__block">
        <ul class="common__toggles">
          <li v-for="toggle in toggles" :key="toggle.id" class="common__toggle">
            <UIToggler v-model="toggle.checked" :text="toggle.name" />
          </li>
        </ul>
      </div>
      <div class="common__buttons">
        <UIButton
          type="button"
          :text="t('apply')"
          :aria-label="t('apply')"
          large-height
          @click="applyFilters"
        />
        <UIButton
          type="button"
          :text="t('clearFilter')"
          :aria-label="t('clearFilter')"
          gray
          large-height
          @click="clearFilters"
        />
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
.common {
  position: relative;
  border: 1px solid @color-alto;
  border-radius: @size-x4;
  background: #fff;
  padding: @size-x5 @size-x4;

  &__filters {
    .flex-column(@size-x4);
  }

  &__block {
    .flex-column(@size-x4);
  }

  &__title {
    font-size: @size-x5;
    font-weight: 600;
    line-height: 100%;

    &::after {
      content: '';
      display: block;
      height: 1px;
      background: @color-alto;
      margin: @size-x4 -@size-x4;
    }
  }

  &__label {
    font-size: 14px;
    font-weight: 600;
    line-height: 130%;
  }

  &__brands {
    .flex-column(@size-x3);
  }

  &__buttons {
    .flex-column(@size-x2);
  }

  &__button {
    &._close {
      display: none;
    }
  }

  &__toggles {
    .flex-column(10px);
  }

  &__toggle {
    padding: 10px @size-x4 0;
    margin: 0 -@size-x4;
    border-top: 1px solid @color-alto;

    &:last-child {
      padding-bottom: @size-x3;
    }
  }

  @media @1024 {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 99;
    border-radius: 0;
    border: 0;
    width: 100%;
    height: 100dvh;
    overflow: auto;
    padding-bottom: 80px;

    &::-webkit-scrollbar {
      width: 0;
    }

    &__buttons {
      position: sticky;
      bottom: 0;
    }

    &__button {
      &._close {
        .button-reset();

        display: block;
        position: absolute;
        top: @size-x4;
        right: @size-x4;
      }
    }

    &__icon {
      &._close {
        width: @size-x5;
        height: @size-x5;
      }
    }
  }
}
</style>
